import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canAccessAdmin } from '../../utils/roleUtils';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import AdminPanel from '../../components/admin/AdminPanel';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import agencyAdminService from '../../services/agencyAdminService';
import { decodeHtmlEntities } from '../../utils/textUtils';
import usePixieDustNavigation from '../../hooks/usePixieDustNavigation';
import { 
  Activity, 
  Search, 
  Filter, 
  Calendar,
  User,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import '../../styles/admin-theme.css';

const ActivityLog = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { navigateToUnifiedItinerary } = usePixieDustNavigation();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActionType, setFilterActionType] = useState('all');
  const [filterUserId, setFilterUserId] = useState('all');
  const [filterTripId, setFilterTripId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Check if user has agency admin access (owner-only)
  // This page is restricted to agentAdmin and parkProAdmin roles
  const hasAgencyAdminAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  // Redirect non-owners immediately
  useEffect(() => {
    if (user && !hasAgencyAdminAccess()) {
      console.warn('Non-owner attempted to access Admin Log', { userId: user.id, role: user.role });
      navigate('/agent/dashboard');
      return;
    }
  }, [user, navigate]);

  const fetchActivityLog = useCallback(async (refresh = false, page = 1) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setFetchError(null);

      const filters = {
        page,
        limit: 50,
        sortBy: 'timestamp',
        sortOrder: 'DESC'
      };

      if (startDate) {
        filters.startDate = startDate;
      }

      if (endDate) {
        filters.endDate = endDate;
      }

      if (filterActionType && filterActionType !== 'all') {
        filters.actionType = filterActionType;
      }

      if (filterUserId && filterUserId !== 'all') {
        filters.userId = filterUserId;
      }

      if (filterTripId && filterTripId.trim()) {
        filters.tripId = filterTripId.trim();
      }

      if (searchQuery && searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const response = await agencyAdminService.getActivityLog(filters);

      if (response.success && response.data) {
        setLogs(response.data.logs || []);
        setPagination(response.data.pagination || {
          page: 1,
          limit: 50,
          total: 0,
          totalPages: 0
        });
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error('Error fetching activity log:', error);
      setFetchError('Unable to load activity log. Please try again.');
      setLogs([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [startDate, endDate, filterActionType, filterUserId, filterTripId, searchQuery]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasAgencyAdminAccess()) {
      navigate('/agent/dashboard');
      return;
    }
    fetchActivityLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Debounced search refetch
  useEffect(() => {
    if (!hasAgencyAdminAccess()) return;
    
    const debounceTimer = setTimeout(() => {
      fetchActivityLog(true, 1);
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, fetchActivityLog, hasAgencyAdminAccess]);

  // Refetch when filters change
  useEffect(() => {
    if (!hasAgencyAdminAccess()) return;
    fetchActivityLog(true, 1);
  }, [filterActionType, filterUserId, filterTripId, startDate, endDate, fetchActivityLog, hasAgencyAdminAccess]);

  const handlePageChange = (newPage) => {
    fetchActivityLog(false, newPage);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '—';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '—';
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      return formatTimestamp(timestamp);
    } catch {
      return '—';
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'trip_created':
        return '📝';
      case 'itinerary_generated':
        return '✨';
      case 'itinerary_update':
      case 'itinerary_edited':
        return '✏️';
      case 'trip_updated':
        return '🔄';
      default:
        return '📋';
    }
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'trip_created': 'Trip Created',
      'itinerary_generated': 'Itinerary Generated',
      'itinerary_update': 'Itinerary Updated',
      'itinerary_edited': 'Itinerary Edited',
      'trip_updated': 'Trip Updated',
      'login': 'Login',
      'logout': 'Logout'
    };
    return labels[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get unique action types and user IDs for filters
  const uniqueActionTypes = [...new Set(logs.map(log => log.action?.type).filter(Boolean))];
  const uniqueUserIds = [...new Set(logs.map(log => log.user?.id).filter(Boolean))];

  if (!hasAgencyAdminAccess()) {
    return null;
  }

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Activity Log"
        subtitle="View all agency activity: who did what, when, and on which trip"
        icon={<Activity className="w-6 h-6" />}
        actions={
          <button
            onClick={() => fetchActivityLog(true, pagination.page)}
            disabled={refreshing}
            className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      <AdminPanel>
        {/* Filters */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by agent, action, trip..."
                  className="py-2 pr-4 pl-10 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Action Type Filter */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Action Type
              </label>
              <select
                value={filterActionType}
                onChange={(e) => setFilterActionType(e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Actions</option>
                {uniqueActionTypes.map(type => (
                  <option key={type} value={type}>{getActionLabel(type)}</option>
                ))}
              </select>
            </div>

            {/* User Filter */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Agent
              </label>
              <select
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Agents</option>
                {uniqueUserIds.map(userId => {
                  const log = logs.find(l => l.user?.id === userId);
                  return log ? (
                    <option key={userId} value={userId}>
                      {log.user?.name || log.user?.email || userId}
                    </option>
                  ) : null;
                })}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Trip ID Filter */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Trip ID
              </label>
              <input
                type="text"
                value={filterTripId}
                onChange={(e) => setFilterTripId(e.target.value)}
                placeholder="Filter by trip ID..."
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Activity Log Table */}
        {loading && !refreshing ? (
          <div className="p-12 text-center">
            <RefreshCw className="mx-auto mb-4 w-8 h-8 text-gray-400 animate-spin" />
            <p className="text-gray-500">Loading activity log...</p>
          </div>
        ) : fetchError ? (
          <AdminEmptyState
            icon={<AlertCircle className="w-12 h-12 text-red-400" />}
            title="Error Loading Activity Log"
            message={fetchError}
            action={
              <button
                onClick={() => fetchActivityLog(true, pagination.page)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Try Again
              </button>
            }
          />
        ) : logs.length === 0 ? (
          <AdminEmptyState
            icon={<Activity className="w-12 h-12 text-gray-400" />}
            title="No Activity Found"
            message="No activity logs match your current filters. Try adjusting your search or date range."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Action
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Trip
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        <div className="font-medium">{formatTimestamp(log.timestamp)}</div>
                        <div className="text-xs text-gray-500">{formatTimeAgo(log.timestamp)}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        <div className="flex gap-2 items-center">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="font-medium">{log.user?.name || 'System'}</div>
                            {log.user?.email && (
                              <div className="text-xs text-gray-500">{log.user.email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">
                        <div className="flex gap-2 items-center">
                          <span className="text-lg">{getActionIcon(log.action?.type)}</span>
                          <span className="font-medium text-gray-900">
                            {getActionLabel(log.action?.type)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex gap-2 items-center">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span>{log.action?.resourceName || '—'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {log.trip ? (
                          <button
                            onClick={() => navigateToUnifiedItinerary(log.trip.id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {decodeHtmlEntities(log.trip.name) || log.trip.id}
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} entries
                </div>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </AdminPanel>
    </div>
  );
};

export default ActivityLog;
